import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";

export const PLANUSER = {
  END_USER: "endUser",
  AGENCY: "agency",
};
const baseUrl = getHostDomain();

// Display order for feature groups in the plan card.
export const GROUP_ORDER = [
  "Usage limits",
  "Channels",
  "Automations",
  "Advanced",
  "Support",
];

// Resolve a possibly-dotted metadata key against a plan row.
// "bots_count" → plan.bots_count
// "credits.workflows" → plan.credits.workflows
// "credits.channels.twilio" → plan.credits.channels.twilio
function resolveByPath(plan, dottedKey) {
  return dottedKey
    .split(".")
    .reduce(
      (obj, k) => (obj && obj[k] !== undefined ? obj[k] : undefined),
      plan
    );
}

// Build the per-plan structured features:
//   { byKey: {<metadata.key>: rawValue},   // raw lookup for cross-tier deltas
//     groups: [{ name, items: [{key, label, value, type}] }] }
// Items within a group: numeric first, then boolean.
export function getStructuredFeatures(plan, metadata) {
  const byKey = {};
  const grouped = {};
  for (const m of metadata || []) {
    let value = resolveByPath(plan, m.key);
    let label = m.label;
    if (m.key === "bots_count" && value === 999999) {
      label = "Unlimited Bots";
      value = true;
    }
    byKey[m.key] = value === undefined ? null : value;
    const group = m.group || "Advanced";
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push({ key: m.key, label, value: byKey[m.key], type: m.type });
  }
  const groups = GROUP_ORDER.filter((g) => grouped[g]).map((g) => ({
    name: g,
    items: grouped[g].sort((a, b) => {
      if (a.type === b.type) return 0;
      if (a.type === "number") return -1;
      return 1;
    }),
  }));
  // Forward-compat: include any unknown groups at the end
  for (const g of Object.keys(grouped)) {
    if (!GROUP_ORDER.includes(g)) {
      groups.push({ name: g, items: grouped[g] });
    }
  }
  return { byKey, groups };
}

// Legacy flat-shape helper, kept as a thin shim so any unknown caller
// passing the old metadata-map shape doesn't break.
export function getFormattedFeatures(plan, metadataOrMap) {
  const metadata = Array.isArray(metadataOrMap)
    ? metadataOrMap
    : Object.entries(metadataOrMap || {}).map(([key, label]) => ({
        key,
        label,
        type: typeof resolveByPath(plan, key) === "number" ? "number" : "boolean",
      }));
  const { byKey } = getStructuredFeatures(plan, metadata);
  const flat = {};
  for (const m of metadata) {
    let label = m.label;
    if (m.key === "bots_count" && byKey[m.key] === true) label = "Unlimited Bots";
    flat[label] = byKey[m.key];
  }
  return flat;
}

export const planUpgradeOrDowngrade = (
  nextPlanId,
  currentPlanId,
  billingPlans = []
) => {
  const currentPlanIndex = billingPlans
    .map((billingPlan) => billingPlan.id)
    .indexOf(currentPlanId);

  const decideUpgradeOrDowngrade = (acc, prev, index) => {
    if (nextPlanId && nextPlanId === prev.id) {
      acc[prev.plan_lookup_key] = "Active from next billing cycle";
      return acc;
    }
    acc[prev.plan_lookup_key] =
      currentPlanIndex >= 0
        ? index > currentPlanIndex
          ? "upgrade"
          : "downgrade"
        : "switch";
    return acc;
  };

  const allBillingPlansLookupKeys = billingPlans.reduce(
    decideUpgradeOrDowngrade,
    {}
  );

  return allBillingPlansLookupKeys;
};

export const fetchBillingData = async () => {
  try {
    const [billingPlanRes, metadataRes, userDetailsRes] = await Promise.all([
      fetchController(
        `${baseUrl}/api/v1/billing/get_active_billing_plans`,
        "GET"
      ),
      fetchController(
        `${baseUrl}/api/v1/billing/get_all_metadata_billing_plans`,
        "GET"
      ),
      fetchController(`${baseUrl}/api/v1/user`, "GET"),
    ]);

    return {
      billingPlansData: billingPlanRes.data,
      // Pass full metadata through so the card can use group + type info.
      // metadataMap kept (key→label) for backward compatibility with
      // any caller still consuming the legacy shape.
      metadata: metadataRes.metadata || [],
      metadataMap: Object.fromEntries(
        (metadataRes.metadata || []).map((meta) => [meta.key, meta.label])
      ),
      userDetails: userDetailsRes.data,
    };
  } catch (error) {
    console.error("Error fetching billing data:", error);
    return null;
  }
};

export const processBillingPlans = (
  billingPlansData,
  userDetails,
  metadataMap,
  metadata = null
) => {
  const activePlan = billingPlansData.items.find(
    (plan) => plan.id === userDetails.active_billing_plan_id
  );
  const isSpecialPlan = !activePlan;
  const selectPlan = planUpgradeOrDowngrade(
    userDetails.next_billing_plan_id,
    userDetails.active_billing_plan_id,
    billingPlansData.items
  );

  return billingPlansData.items.map((plan) => {
    const isActive = isSpecialPlan
      ? false
      : userDetails.active_billing_plan_id === plan.id ||
        plan.plan_lookup_key === "insightoai_free";

    const isDisable =
      isSpecialPlan && plan.plan_lookup_key === "insightoai_free"
        ? true
        : userDetails.is_trial && activePlan?.queries_count > plan.queries_count
        ? true
        : userDetails?.next_billing_plan_id === plan.id
        ? true
        : false;
    return {
      id: plan.id,
      plan: plan.plan_lookup_key,
      isActive,
      label: selectPlan[plan.plan_lookup_key],
      isDisable,
      // Legacy flat shape (some callers still read features[0])
      features: [getFormattedFeatures(plan, metadataMap)],
      // New structured shape (BillingCard reads this for grouped render)
      structuredFeatures: metadata
        ? getStructuredFeatures(plan, metadata)
        : null,
      pricing: plan.description,
      popular: plan.attributes && plan.attributes.is_popular === true,
      name: plan.name,
      is_trial: userDetails.is_trial,
      description: plan.description,
      erlyBirdsOffer:
        plan.plan_lookup_key === "insightoai_gold" &&
        process.env.REACT_APP_TRIAL_DURATION
          ? `Early Adopters - ${process.env.REACT_APP_TRIAL_DURATION} free`
          : "",
    };
  });
};

export const fetchCouponPlan = async (userDetails, metadataMap, metadata = null) => {
  try {
    // const specialPlanRes = await fetchController(
    //   `${baseUrl}/api/v1/billing/billing_plan/${userDetails.active_billing_plan_id}`,
    //   "GET"
    // );
 const specialPlanRes = await fetchController(
        baseUrl +
          `/api/v1/billing/effective_billing_plan`,
        "GET"
      );
      // console.log(billingPlanRes,"<><>")
    return {
      ...specialPlanRes.data,
      plan: specialPlanRes.data.plan_lookup_key,
      isActive: true,
      pricing: specialPlanRes.data.description,
      label: "Activated",
      features: [getFormattedFeatures(specialPlanRes.data, metadataMap)],
      structuredFeatures: metadata
        ? getStructuredFeatures(specialPlanRes.data, metadata)
        : null,
      popular: specialPlanRes.data.plan_lookup_key === "insightoai_platinum",
    };
  } catch (error) {
    console.error("Error fetching special plan:", error);
    return null;
  }
};

// Helper used by GroupSection / union logic: is a value "active"?
const _isActivated = (v) => {
  if (v === true) return true;
  if (typeof v === "number" && v >= 1) return true;
  if (typeof v === "string" && v.length > 0) return true;
  return false;
};

// Compute the union of features that are activated in at least one of the
// given plans, organised by group in GROUP_ORDER. Used by PlansSection to
// drive a SYNCHRONISED comparison layout: every card renders exactly this
// list of items in the same order so rows line up across columns and cards
// naturally end up the same height.
//
// Items that no plan activates are dropped entirely (so e.g. SIP support
// won't appear at all if no listed plan has it on).
//
// Returns: [{ name, items: [{ key, label, type }] }, ...]
export function computeUnionByGroup(plans) {
  const seen = new Map();
  for (const plan of plans || []) {
    const sf = plan?.structuredFeatures;
    if (!sf) continue;
    for (const g of sf.groups) {
      for (const item of g.items) {
        if (!_isActivated(item.value)) continue;
        const cacheKey = `${g.name}::${item.key}`;
        if (!seen.has(cacheKey)) {
          seen.set(cacheKey, {
            group: g.name,
            key: item.key,
            label: item.label,
            type: item.type,
          });
        }
      }
    }
  }
  const grouped = {};
  for (const v of seen.values()) {
    if (!grouped[v.group]) grouped[v.group] = [];
    grouped[v.group].push({ key: v.key, label: v.label, type: v.type });
  }
  const out = [];
  for (const g of GROUP_ORDER) {
    if (grouped[g]) {
      out.push({
        name: g,
        items: grouped[g].sort((a, b) => {
          if (a.type === b.type) return 0;
          return a.type === "number" ? -1 : 1;
        }),
      });
    }
  }
  // Forward-compat for any group not in GROUP_ORDER
  for (const g of Object.keys(grouped)) {
    if (!GROUP_ORDER.includes(g)) out.push({ name: g, items: grouped[g] });
  }
  return out;
}
